import './AttachmentsPreview.scss';
import { prefixClass, showToast, TEN_SECONDS, TOAST_ERROR } from 'lib/utils';

import React, { FC, MouseEvent, useState } from 'react';

import type { AttachmentsPreviewExtra } from 'presentation/components/KSQ2/Cards/MessagesCard/subcomponents/MessageContent/analyzeMessageContent';
import MessageAttachmentModal from 'presentation/components/Queue/common/MessageAttachments/MessageAttachmentModal/MessageAttachmentModal';
import { IconFile, IconDocument, IconDownload } from 'components/Icons';
import AttachmentThumbnail from './AttachmentThumbnail';
import { cleanFilename } from 'presentation/components/Queue/utils/fileUtils';
import { getQueueAttachmentBlob } from 'presentation/components/Queue/common/MessageAttachments/attachmentDownloadUtils';
import TooltipWrapper from 'presentation/components/Queue/common/TooltipWrapper/TooltipWrapper';

type PrefixClassName = (element?: string) => string;

export interface AttachmentsPreviewProps {
  attachments: AttachmentsPreviewExtra['attachments'];
}

const getFileIcon = (fileType: string | undefined) => {
  if (!fileType) return <IconFile />;
  const type = fileType.toLowerCase();

  // Use IconDocument for all document types for now
  if (type === 'pdf' || type === 'xls' || type === 'xlsx' || type === 'csv' || type === 'doc' || type === 'docx') {
    return <IconDocument />;
  }

  return <IconFile />;
};

const isImageType = (fileType: string | undefined): boolean => {
  if (!fileType) return false;
  const imageTypes = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'tiff', 'svg'];
  return imageTypes.includes(fileType.toLowerCase());
};

const AttachmentsPreview: FC<AttachmentsPreviewProps> = ({ attachments }) => {
  const prefix = prefixClass('q2-message-preview-module-attachments');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState<string | null>(null);

  // Show all attachments in the grid - let CSS handle the layout
  const displayAttachments = attachments;
  const remainingCount = 0; // No longer limiting display

  const handleAttachmentClick = (index: number) => {
    setSelectedIndex(index);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleDownloadAttachment = async (
    event: MouseEvent<HTMLButtonElement>,
    attachment: AttachmentsPreviewExtra['attachments'][number]
  ) => {
    event.stopPropagation();

    try {
      setDownloadingAttachmentId(attachment.attachmentId);
      const blob = await getQueueAttachmentBlob(attachment);
      if (!blob) return;

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = cleanFilename(attachment.filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Queue attachment download failed:', error);
      showToast('Failed to download attachment. Please try again.', TOAST_ERROR, TEN_SECONDS);
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  return (
    <>
      <div className={prefix()}>
        {displayAttachments.map((attachment, index) => {
          const isImage = isImageType(attachment.fileType);

          return (
            <div key={attachment.attachmentId} className={prefix('item')} onClick={() => handleAttachmentClick(index)}>
              {isImage ? (
                <AttachmentThumbnail attachment={attachment} />
              ) : (
                <div className={prefix('item-document')}>
                  <div className={prefix('item-document-icon')}>{getFileIcon(attachment.fileType)}</div>
                  <div className={prefix('item-document-info')}>
                    <div className={prefix('item-document-name')}>{attachment.filename}</div>
                    <div className={prefix('item-document-type')}>{attachment.fileType?.toUpperCase()}</div>
                  </div>
                </div>
              )}
              <TooltipWrapper tooltip="Download attachment" delay={300}>
                <button
                  className={prefix('item-download')}
                  type="button"
                  aria-label={`Download ${cleanFilename(attachment.filename)}`}
                  disabled={downloadingAttachmentId === attachment.attachmentId}
                  onClick={event => handleDownloadAttachment(event, attachment)}
                >
                  <IconDownload />
                </button>
              </TooltipWrapper>
            </div>
          );
        })}
      </div>

      {modalOpen && (
        <MessageAttachmentModal attachments={attachments.slice(selectedIndex)} handleClose={handleCloseModal} />
      )}
    </>
  );
};

export default AttachmentsPreview;
